import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import { Bank, Plus, LinkSimple, LinkBreak, MagicWand, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ReconSessionPanel from "@/pages/finance/ReconSessionPanel";

const money = (v) => formatCurrency(v);

function ImportDialog({ open, onOpenChange, bankCode, onDone }) {
  const [text, setText] = useState("");
  const submit = async () => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [date, description, amount] = l.split(",").map((x) => (x || "").trim());
      return { date, description, amount: parseFloat(amount) || 0 };
    }).filter((l) => l.date && l.amount);
    if (!lines.length) { toast.error("No valid lines — format: YYYY-MM-DD, description, amount"); return; }
    try {
      const { data } = await api.post("/finance/gl/bank-recon/statement-lines", { bank_code: bankCode, lines });
      toast.success(`${data.imported} statement line(s) imported`);
      setText(""); onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add bank statement lines</DialogTitle>
          <DialogDescription>One line per row: <code>YYYY-MM-DD, description, amount</code> (positive = deposit, negative = withdrawal)</DialogDescription>
        </DialogHeader>
        <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={"2026-07-01, Client transfer ACME, 5000\n2026-07-03, Bank charges, -25.50"}
          data-testid="bankrec-import-text" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary-blue" onClick={submit} data-testid="bankrec-import-save">Import lines</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GLBankMatchTab({ canEdit }) {
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState("1120");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [stmts, setStmts] = useState([]);
  const [glLines, setGlLines] = useState([]);
  const [selStmt, setSelStmt] = useState(null);
  const [selGl, setSelGl] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = { bank_code: bankCode, ...(period && { period }) };
      const [b, s, st, gl] = await Promise.all([
        api.get("/finance/gl/bank-recon/banks"),
        api.get("/finance/gl/bank-recon/summary", { params }),
        api.get("/finance/gl/bank-recon/statement-lines", { params }),
        api.get("/finance/gl/bank-recon/gl-lines", { params }),
      ]);
      setBanks(b.data.banks); setSummary(s.data); setStmts(st.data.items); setGlLines(gl.data.items);
      setSelStmt(null); setSelGl([]);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [bankCode, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const doMatch = async () => {
    if (!selStmt || !selGl.length) return;
    try {
      await api.post("/finance/gl/bank-recon/match", { statement_line_id: selStmt, journal_line_ids: selGl });
      toast.success("Matched ✓"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unmatch = async (id) => {
    try { await api.post(`/finance/gl/bank-recon/statement-lines/${id}/unmatch`); toast.success("Unmatched"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeLine = async (id) => {
    try { await api.delete(`/finance/gl/bank-recon/statement-lines/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const autoMatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/gl/bank-recon/auto-match", { bank_code: bankCode, period: period || null });
      toast.success(`Auto-matched ${data.matched} of ${data.statement_lines_scanned} statement line(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const glSelSum = glLines.filter((l) => selGl.includes(l.id)).reduce((a, l) => a + l.signed_amount, 0);
  const stmtSel = stmts.find((s) => s.id === selStmt);
  const canMatch = selStmt && selGl.length > 0 && stmtSel && Math.abs(glSelSum - stmtSel.amount) <= 0.01;

  return (
    <div className="space-y-4" data-testid="gl-bankmatch-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Bank account</Label>
            <Select value={bankCode} onValueChange={setBankCode}>
              <SelectTrigger className="h-8 w-56" data-testid="bankrec-bank-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(banks.length ? banks : [{ code: "1120", name: "Bank — Premier" }]).map((b) => (
                  <SelectItem key={b.code} value={b.code}>{b.code} · {b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Period</Label>
            <Input type="month" className="h-8 w-40" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="bankrec-period" />
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={autoMatch} disabled={busy} data-testid="bankrec-automatch-btn">
              <MagicWand size={12} className="mr-1" /> Auto-match
            </Button>
            <Button size="sm" className="btn-primary-blue" onClick={() => setImportOpen(true)} data-testid="bankrec-import-btn">
              <Plus size={12} className="mr-1" /> Add statement lines
            </Button>
          </div>
        )}
      </div>

      <ReconSessionPanel bankCode={bankCode} period={period} canEdit={canEdit} onChanged={load} />

      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="bankrec-summary">
          <div className="dc-card p-3"><div className="overline">GL balance</div><div className="font-display text-lg font-bold tabular-nums">{money(summary.gl_balance)}</div></div>
          <div className="dc-card p-3"><div className="overline">GL movement</div><div className="font-display text-lg font-bold tabular-nums">{money(summary.gl_movement)}</div><div className="text-[10px] text-slate-400">{summary.gl_line_count} lines</div></div>
          <div className="dc-card p-3"><div className="overline">Statement total</div><div className="font-display text-lg font-bold tabular-nums">{money(summary.statement_total)}</div><div className="text-[10px] text-slate-400">{summary.statement_count} lines</div></div>
          <div className="dc-card p-3"><div className="overline">Reconciled</div><div className="font-display text-lg font-bold tabular-nums text-emerald-600" data-testid="bankrec-pct">{summary.reconciled_pct}%</div><div className="text-[10px] text-slate-400">{summary.matched_count} matched</div></div>
          <div className="dc-card p-3"><div className="overline">Difference</div><div className={`font-display text-lg font-bold tabular-nums ${Math.abs(summary.difference) <= 0.01 ? "text-slate-900" : "text-amber-600"}`}>{money(summary.difference)}</div></div>
        </div>
      )}

      {selStmt && (
        <div className="dc-card flex flex-wrap items-center gap-3 p-3" data-testid="bankrec-match-bar">
          <span className="text-xs font-semibold">Selected: statement {money(stmtSel?.amount || 0)} · GL {money(glSelSum)} ({selGl.length} line{selGl.length !== 1 ? "s" : ""})</span>
          <span className={`text-xs font-bold ${canMatch ? "text-emerald-600" : "text-rose-600"}`}>{canMatch ? "Amounts equal ✓" : "Amounts differ"}</span>
          <Button size="sm" className="btn-primary-blue h-7" onClick={doMatch} disabled={!canMatch} data-testid="bankrec-match-btn">
            <LinkSimple size={12} className="mr-1" /> Match
          </Button>
          <Button size="sm" variant="outline" className="h-7" onClick={() => { setSelStmt(null); setSelGl([]); }}>Clear</Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-2.5 flex items-center justify-between">
            <span className="overline">Bank statement lines</span>
          </div>
          {stmts.length === 0 ? (
            <EmptyState icon={Bank} title="No statement lines" description="Add statement lines to start matching against the ledger." />
          ) : (
            <div className="max-h-[480px] overflow-y-auto">
              <table className="data-table w-full">
                <thead><tr><th>Date</th><th>Description</th><th className="text-right">Amount</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {stmts.map((s) => (
                    <tr key={s.id}
                      className={`cursor-pointer transition ${selStmt === s.id ? "bg-blue-50" : "hover:bg-slate-50/70"} ${s.status === "matched" ? "opacity-60" : ""}`}
                      onClick={() => s.status === "unmatched" && setSelStmt(selStmt === s.id ? null : s.id)}
                      data-testid={`bankrec-stmt-${s.id}`}>
                      <td className="font-mono-alt text-xs tabular-nums">{formatDate(s.date)}</td>
                      <td className="text-xs max-w-[180px] truncate">{s.description}</td>
                      <td className={`text-right font-mono-alt tabular-nums text-xs ${s.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>{money(s.amount)}</td>
                      <td>
                        <span className={`text-[9px] font-semibold uppercase tracking-wider ${s.status === "matched" ? "text-emerald-600" : "text-amber-600"}`}>{s.status}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {canEdit && (s.status === "matched" ? (
                          <Button size="sm" variant="outline" className="h-6 px-1.5" title="Unmatch" onClick={() => unmatch(s.id)} data-testid={`bankrec-unmatch-${s.id}`}>
                            <LinkBreak size={10} />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-6 px-1.5" title="Delete" onClick={() => removeLine(s.id)}>
                            <Trash size={10} />
                          </Button>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-2.5">
            <span className="overline">GL cash lines · account {bankCode}</span>
          </div>
          {glLines.length === 0 ? (
            <EmptyState icon={Bank} title="No GL activity" description="No posted journal lines on this account for the selected period." />
          ) : (
            <div className="max-h-[480px] overflow-y-auto">
              <table className="data-table w-full">
                <thead><tr><th /><th>Date</th><th>Entry</th><th>Description</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {glLines.map((l) => (
                    <tr key={l.id}
                      className={`transition ${l.matched ? "opacity-50" : "cursor-pointer hover:bg-slate-50/70"} ${selGl.includes(l.id) ? "bg-blue-50" : ""}`}
                      onClick={() => !l.matched && setSelGl((xs) => xs.includes(l.id) ? xs.filter((x) => x !== l.id) : [...xs, l.id])}
                      data-testid={`bankrec-gl-${l.id}`}>
                      <td className="w-6">
                        <input type="checkbox" readOnly checked={selGl.includes(l.id)} disabled={l.matched} className="accent-blue-600" />
                      </td>
                      <td className="font-mono-alt text-xs tabular-nums">{formatDate(l.date)}</td>
                      <td className="font-mono-alt text-xs tabular-nums">{l.entry_no}</td>
                      <td className="text-xs max-w-[160px] truncate">{l.description || "—"}</td>
                      <td className={`text-right font-mono-alt tabular-nums text-xs ${l.signed_amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                        {money(l.signed_amount)}
                        {l.matched && <span className="ml-1 text-[8px] uppercase text-emerald-600">✓</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} bankCode={bankCode} onDone={load} />
    </div>
  );
}
